import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import { entityService } from '@/services/EntityService';
import { getCatalogLocationLabels } from '@/hooks/useCatalogLocationLabels';
import {
  buildPrimaryRestockReportHtml,
  type PrimaryRestockItem,
  type PrimaryRestockTemplateOptions,
} from '@/reports/templates/primaryRestockTemplate';

interface AssetInventorySummaryRow {
  id: string;
  asset_name: string;
  location_id: string | null;
  location_name: string;
  location_path: string;
  lin: string | null;
  nsn: string | null;
  status_key: string | null;
  is_true_location: boolean;
  stock_quantity: number;
  asset_ids: string[];
  catalog_id: string | null;
}

interface LocationNode {
  id: string;
  displayName: string;
  entityIdentifier: string | null;
  parentLocationId: string | null;
}

interface CatalogThresholds {
  minPrimaryQty: number | null;
}

interface PrimaryAggregate {
  catalogId: string | null;
  lin: string | null;
  nsn: string | null;
  nomenclature: string;
  onHandQuantity: number;
}

interface StorageAggregate {
  quantity: number;
  sites: Set<string>;
  locations: Set<string>;
  sublocations: Set<string>;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parseInteger = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseStockQuantity = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  return 0;
};

const normalizeForMatch = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.toLowerCase().replace(/[\s_-]+/g, '');
};

const isPrimaryLocationName = (value: string | null | undefined): boolean => {
  const normalized = normalizeForMatch(value);
  return normalized.includes('primary');
};

const buildLocationLookup = (
  locations: Array<{
    id: string;
    display_name: string;
    entity_identifier?: string;
    entity_data: Record<string, unknown>;
  }>,
): Map<string, LocationNode> => {
  const lookup = new Map<string, LocationNode>();

  locations.forEach((location) => {
    const parentLocationId = coerceString(location.entity_data.parent_location_id);
    lookup.set(location.id, {
      id: location.id,
      displayName: location.display_name,
      entityIdentifier: coerceString(location.entity_identifier),
      parentLocationId,
    });
  });

  return lookup;
};

const getLocationChain = (
  locationId: string,
  locationLookup: Map<string, LocationNode>,
  chainCache: Map<string, LocationNode[]>,
): LocationNode[] => {
  const cached = chainCache.get(locationId);
  if (cached) {
    return cached;
  }

  const chain: LocationNode[] = [];
  const seen = new Set<string>();
  let cursor = locationLookup.get(locationId);

  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    chain.push(cursor);
    cursor = cursor.parentLocationId ? locationLookup.get(cursor.parentLocationId) : undefined;
  }

  chain.reverse();
  chainCache.set(locationId, chain);
  return chain;
};

const isPrimaryLocation = (
  locationId: string | null,
  locationLookup: Map<string, LocationNode>,
  chainCache: Map<string, LocationNode[]>,
): boolean => {
  if (!locationId) return false;
  const chain = getLocationChain(locationId, locationLookup, chainCache);

  return chain.some((node) => {
    if (isPrimaryLocationName(node.displayName)) return true;
    const normalizedIdentifier = normalizeForMatch(node.entityIdentifier);
    return normalizedIdentifier.includes('sitesf');
  });
};

const extractLocationParts = (
  row: AssetInventorySummaryRow,
  locationLookup: Map<string, LocationNode>,
  chainCache: Map<string, LocationNode[]>,
): { site: string | null; location: string | null; sublocation: string | null } => {
  const locationId = coerceString(row.location_id);
  if (locationId) {
    const chain = getLocationChain(locationId, locationLookup, chainCache).map(
      (node) => node.displayName,
    );
    if (chain.length > 0) {
      return {
        site: chain[0] ?? null,
        location: chain[1] ?? null,
        sublocation: chain.length > 2 ? chain.slice(2).join(' > ') : null,
      };
    }
  }

  const pathParts = row.location_path
    .split('>')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  return {
    site: pathParts[0] ?? null,
    location: pathParts[1] ?? null,
    sublocation: pathParts.length > 2 ? pathParts.slice(2).join(' > ') : null,
  };
};

const parseCatalogThresholds = (
  entityData: Record<string, unknown> | null | undefined,
): CatalogThresholds => {
  const minPrimaryQty = parseInteger(entityData?.storefront_minimum);
  return {
    minPrimaryQty,
  };
};

const joinValues = (values: Set<string>): string | null => {
  if (values.size === 0) return null;
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
};

export const runPrimaryRestockReport = async (
  params: Record<string, unknown> = {},
): Promise<void> => {
  const query = new URLSearchParams({
    status_key: `eq.${ASSET_STATUS_KEYS.AVAILABLE}`,
    is_true_location: 'eq.true',
    limit: '5000',
  });
  // Inventory rows and location list are independent — fetch in parallel.
  const [rows, locations] = await Promise.all([
    httpClient.get<AssetInventorySummaryRow[]>(
      `/api/rest/data/v1/asset_inventory_summary?${query.toString()}`,
    ),
    entityService.list({
      type: 'location',
      limit: 5000,
      order: 'id.asc',
      filters: {
        select: 'id,display_name,entity_identifier,entity_data',
      },
    }),
  ]);

  const locationLookup = buildLocationLookup(
    locations.map((location) => ({
      id: location.id,
      display_name: location.display_name,
      entity_identifier: location.entity_identifier,
      entity_data: (location.entity_data ?? {}) as Record<string, unknown>,
    })),
  );
  const locationChainCache = new Map<string, LocationNode[]>();

  const primaryRows: AssetInventorySummaryRow[] = [];
  const storageRows: AssetInventorySummaryRow[] = [];
  rows.forEach((row) => {
    if (isPrimaryLocation(row.location_id, locationLookup, locationChainCache)) {
      primaryRows.push(row);
      return;
    }
    storageRows.push(row);
  });

  const primaryByCatalogKey = new Map<string, PrimaryAggregate>();
  primaryRows.forEach((row) => {
    const catalogId = coerceString(row.catalog_id);
    const key = catalogId ?? `${row.lin ?? ''}|${row.nsn ?? ''}|${row.asset_name}`;
    const existing = primaryByCatalogKey.get(key);
    const onHandQuantity = parseStockQuantity(row.stock_quantity);

    if (!existing) {
      primaryByCatalogKey.set(key, {
        catalogId,
        lin: coerceString(row.lin),
        nsn: coerceString(row.nsn),
        nomenclature: coerceString(row.asset_name) ?? '—',
        onHandQuantity,
      });
      return;
    }

    existing.onHandQuantity += onHandQuantity;
  });

  // Fetch all catalogs that have the configured minimum field so items with
  // zero on-hand stock still appear on the report.
  const allMinCatalogs = await entityService.list({
    type: 'catalog',
    limit: 5000,
    order: 'id.asc',
    filters: {
      'entity_data->>storefront_minimum': 'not.is.null',
      select: 'id,display_name,entity_data',
    },
  });

  const thresholdsByCatalogId = new Map<string, CatalogThresholds>();
  allMinCatalogs.forEach((catalog) => {
    const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
    const thresholds = parseCatalogThresholds(entityData);
    thresholdsByCatalogId.set(catalog.id, thresholds);

    // Back-fill zero-stock entries for catalogs not already seen in primary inventory.
    if (!primaryByCatalogKey.has(catalog.id)) {
      primaryByCatalogKey.set(catalog.id, {
        catalogId: catalog.id,
        lin: coerceString(entityData.lin as string | null | undefined),
        nsn: coerceString(entityData.nsn as string | null | undefined),
        nomenclature: coerceString(catalog.display_name) ?? '—',
        onHandQuantity: 0,
      });
    }
  });

  // Fetch thresholds for any primary catalog IDs not already covered.
  const remainingCatalogIds = Array.from(
    new Set(
      Array.from(primaryByCatalogKey.values())
        .map((row) => row.catalogId)
        .filter(
          (catalogId): catalogId is string =>
            catalogId != null && !thresholdsByCatalogId.has(catalogId),
        ),
    ),
  );

  for (const catalogIdBatch of chunk(remainingCatalogIds, 75)) {
    const idFilter =
      catalogIdBatch.length === 1 ? `eq.${catalogIdBatch[0]}` : `in.(${catalogIdBatch.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: catalogIdBatch.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    catalogs.forEach((catalog) => {
      thresholdsByCatalogId.set(
        catalog.id,
        parseCatalogThresholds((catalog.entity_data ?? {}) as Record<string, unknown>),
      );
    });
  }

  const storageByCatalogId = new Map<string, StorageAggregate>();
  storageRows.forEach((row) => {
    const catalogId = coerceString(row.catalog_id);
    if (!catalogId) return;

    let aggregate = storageByCatalogId.get(catalogId);
    if (!aggregate) {
      aggregate = {
        quantity: 0,
        sites: new Set<string>(),
        locations: new Set<string>(),
        sublocations: new Set<string>(),
      };
      storageByCatalogId.set(catalogId, aggregate);
    }

    aggregate.quantity += parseStockQuantity(row.stock_quantity);

    const locationParts = extractLocationParts(row, locationLookup, locationChainCache);
    if (locationParts.site) aggregate.sites.add(locationParts.site);
    if (locationParts.location) aggregate.locations.add(locationParts.location);
    if (locationParts.sublocation) aggregate.sublocations.add(locationParts.sublocation);
  });

  const items: PrimaryRestockItem[] = [];

  Array.from(primaryByCatalogKey.values()).forEach((row) => {
    const catalogId = row.catalogId;
    if (!catalogId) return;

    const thresholds = thresholdsByCatalogId.get(catalogId);
    const minPrimaryQty = thresholds?.minPrimaryQty ?? null;
    const onHandQuantity = row.onHandQuantity;

    if (minPrimaryQty == null || onHandQuantity >= minPrimaryQty) {
      return;
    }

    const restockQty = Math.max(minPrimaryQty - onHandQuantity, 0);
    const storage = storageByCatalogId.get(catalogId);

    items.push({
      lin: row.lin,
      nsn: row.nsn,
      nomenclature: row.nomenclature,
      onHandQuantity,
      minPrimaryQty,
      restockQty,
      qtyInStorage: storage?.quantity ?? 0,
      storageSite: storage ? joinValues(storage.sites) : null,
      storageLocation: storage ? joinValues(storage.locations) : null,
      storageSublocation: storage ? joinValues(storage.sublocations) : null,
    });
  });

  items.sort((left, right) => {
    const leftRestock = left.restockQty ?? 0;
    const rightRestock = right.restockQty ?? 0;
    if (rightRestock !== leftRestock) {
      return rightRestock - leftRestock;
    }
    return left.nomenclature.localeCompare(right.nomenclature);
  });

  const catalogLocationLabels = getCatalogLocationLabels(params);

  const options: PrimaryRestockTemplateOptions = {
    generatedAt: new Date(),
    items,
    primaryLocationLabel: catalogLocationLabels.primaryLocationLabel,
    secondaryLocationLabel: catalogLocationLabels.secondaryLocationLabel,
  };

  const html = buildPrimaryRestockReportHtml(options);
  const primaryLocationSlug = catalogLocationLabels.primaryLocationLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const reportFilename = `${primaryLocationSlug || 'primary'}-restock-report.html`;

  outputReport(html, reportFilename, getOutputFormat(params), getPreOpenedWindow(params));
};
